import { StyledTourDetail } from "@components";
import { useEffect, useState } from "react";
import { getFarePrice, useAppDispatch } from "@redux";
import styled from "styled-components";
import { breakpoint, useWindowDimensions } from "@utilities";
import _ from "lodash";
import { faresPricesGroups } from "src/Utilities/cartHelper";
import { ProductPriceGroup } from "./ProductPriceGroup";
import moment from "moment";
import { FARE_PRICE_TRAVEL_DATE_FORMAT } from "@constants";
import { CheckAvailsModal } from "./ProductAvailabilities";
import { AvailabilityRangePicker } from "../../TourCheckAvail";
import {
    BookingPriceGroup,
    CheckProductAvailsDateRange,
    IProductDetail,
    ITourFareBookingTime,
} from "@interfaces";

interface IProps {
    product: IProductDetail;
}

export const ProductPrices = ({ product }: IProps) => {
    const { faresPrices: productPrices, productClass } = product;
    const dispatch = useAppDispatch();
    const { width } = useWindowDimensions();
    const bookingSystem = productPrices?.find((f) => f.hasBookingSystem)?.bookingSystem ?? null;
    const firstFare = _.first(productPrices);
    const now = new Date();

    // Determine the travel end date from `firstFare` if it exists
    const travelEnd = !_.isNil(firstFare?.travelEnd)
        ? moment(firstFare!.travelEnd!, FARE_PRICE_TRAVEL_DATE_FORMAT).toDate()
        : null;

    const isProductExpired = travelEnd ? moment(travelEnd).isBefore(moment()) : false;

    // Set a default "to" date for availability checking
    // If `travelEnd` exists and is within 15 days from today, use `travelEnd`
    // Otherwise, set `to` date as 15 days from today
    const defaultToDate =
        travelEnd &&
        // Null: Indicates we're comparing by millisecond precision (default behavior) rather than a specific granularity like 'day' or 'month'a
        // "[]": Square brackets mean the comparison is inclusive of both start and end dates. This ensures:
        // 1. travelEnd exactly matching either now or now + 14 days will be included
        // 2. Without this, the default exclusive behavior would miss same-day boundaries
        moment(travelEnd).isBetween(moment(now), moment(now).add(15, "days"), null, "[]")
            ? travelEnd
            : moment(now).add(15, "days").toDate();

    // Define state to manage the date range for availability checking
    const [checkAvailDateRange, setCheckAvailDateRange] = useState<CheckProductAvailsDateRange>({
        startDate: now,
        numDays: moment(defaultToDate).diff(moment(now), "days"),
    });

    // Define constraints for date selection
    const limitDate = {
        from: now,
        to: moment(now).add(5, "months").toDate(),
    };

    const [selectedPriceGroupWithBookingTimeId, setSelectedPriceGroupWithBookingTimeId] = useState<{
        priceGroup: BookingPriceGroup;
        bookingTimeId: string;
        bookingDetails: ITourFareBookingTime;
    } | null>(null);

    useEffect(() => {
        if (width < 768 && selectedPriceGroupWithBookingTimeId) {
            document.body.classList.add("hide-beacon-head");
        } else {
            document.body.classList.remove("hide-beacon-head");
        }
        return () => document.body.classList.remove("hide-beacon-head");
    }, [selectedPriceGroupWithBookingTimeId, width]);

    useEffect(() => {
        dispatch(getFarePrice(productPrices[0].productPricesDetailsId, productPrices[0].fareName));
    }, []);

    const productPricesView = (
        <>
            {!_.isNil(productPrices || isProductExpired) ? (
                faresPricesGroups(productPrices).map((priceGroup) => {
                    return (
                        <ProductPriceGroup
                            key={priceGroup.id}
                            priceGroup={priceGroup}
                            range={checkAvailDateRange}
                            bookingSystem={bookingSystem}
                            productClass={productClass}
                            checkAvailability={(selectedBookingTimeId, bookingDetails) => {
                                setSelectedPriceGroupWithBookingTimeId({
                                    priceGroup,
                                    bookingTimeId: selectedBookingTimeId,
                                    bookingDetails,
                                });
                            }}
                        />
                    );
                })
            ) : (
                <p className="noFare">No fare available!</p>
            )}
        </>
    );

    const mobileView = isProductExpired ? (
        <NotAvailable className="noFare">This product is no longer available</NotAvailable>
    ) : (
        <MobilveViewContainer>
            <div className="info">
                <p className="heading">Select Date</p>
                <p className="title">{product.name}</p>
                <div className="choseDate">
                    <DateFIeld>
                        <AvailabilityRangePicker
                            range={checkAvailDateRange}
                            limitDate={limitDate}
                            onRangeChange={(startDate, numDays) =>
                                setCheckAvailDateRange({ startDate, numDays })
                            }
                        />
                    </DateFIeld>
                </div>
            </div>
            <div className="price">{productPricesView}</div>
        </MobilveViewContainer>
    );

    const desktopView = isProductExpired ? (
        <NotAvailable className="noFare">This product is no longer available</NotAvailable>
    ) : (
        <StyledTourDetail>
            <div className="heading">
                <h3>Price</h3>
                <DateFIeld>
                    <AvailabilityRangePicker
                        range={checkAvailDateRange}
                        limitDate={limitDate}
                        onRangeChange={(startDate, numDays) =>
                            setCheckAvailDateRange({ startDate, numDays })
                        }
                    />
                </DateFIeld>
            </div>
            <div>{productPricesView}</div>
        </StyledTourDetail>
    );

    useEffect(() => {
        if (selectedPriceGroupWithBookingTimeId) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [selectedPriceGroupWithBookingTimeId]);

    return (
        <>
            {width < 768 ? mobileView : desktopView}
            {selectedPriceGroupWithBookingTimeId && (
                <CheckAvailsModal
                    {...checkAvailDateRange}
                    productId={product.productId}
                    productClass={productClass!}
                    limitDate={limitDate}
                    isOpen={true}
                    bookingtPriceGroup={selectedPriceGroupWithBookingTimeId.priceGroup}
                    selectedBookingTimeId={selectedPriceGroupWithBookingTimeId.bookingTimeId}
                    bookingDetails={selectedPriceGroupWithBookingTimeId.bookingDetails}
                    apiProviderId={product.apiProviderId}
                    groupFaresForAvailabilityCheck={product.groupFaresForAvailabilityCheck}
                    onClose={() => setSelectedPriceGroupWithBookingTimeId(null)}
                />
            )}
        </>
    );
};

const NotAvailable = styled.p`
    width: ${(props) => props.theme.widths.wide_700};
    margin: 0 auto;
    background-color: #fff;
    margin-top: 50px;
    font-size: 20px;
    font-weight: 700;
    padding: 10px 20px;
    text-align: center;

    ${breakpoint.breakMobile`
        width: 100%;
        font-size: 16px;
    `}

    ${breakpoint.breakIpadPro`
        width: ${(props) => props.theme.widths.wide_900};
    `}

    ${breakpoint.breakLarge`
        width: ${(props) => props.theme.widths.wide_1110};
    `}
`;

const DateFIeld = styled.div`
    display: flex;
    gap: 20px;

    .datePicker {
        .MuiInputLabel-animated {
            color: #fff;
        }

        .MuiFormControl-marginNormal {
            margin: 0;
        }

        .MuiInputBase-root {
            background-color: ${(p) => p.theme.colors.gray_1} !important;
            height: 36px;
            width: 135px;

            ${breakpoint.breakIpadPro`
                width: 160px;
            `}

            ${breakpoint.breakLarge`
                width: 214px;
            `}
        }

        .MuiInputBase-input {
            color: #fff;
        }
    }
`;

const MobilveViewContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;

    .price {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .info {
        border-radius: 8px;
        padding: 20px 16px 16px 16px;
        background-color: #fff;
        .heading {
            font-weight: 700;
            font-size: 16px;
            margin-bottom: 10px;
        }

        .title {
            font-weight: 400;
            font-size: 16px;
            margin-bottom: 16px;
            line-height: 21px;
        }
        .travel-time {
            font-weight: normal;
            line-height: 21px;
            font-size: 14px;
            margin-bottom: 10px;
            color: ${(props) => props.theme.colors.gray_3};
        }
    }

    .choseDate {
        display: flex;
        gap: 20px;

        .MuiInputLabel-animated {
            color: #000;
        }

        .MuiInputBase-input {
            color: #000;
        }
    }

    .listButton {
        display: flex;

        button {
            font-family: DM Sans;
            font-size: 12px;
            font-weight: 700;
            line-height: 19px;
            margin-top: 0;
            border-bottom-right-radius: 4px;
            border-bottom-left-radius: 4px;
        }
    }

    .MuiButtonBase-root span {
        color: #000;
    }
    .MuiRadio-colorSecondary.Mui-checked span {
        color: ${(p) => p.theme.colors.main};
    }
    .MuiInputBase-root {
        margin-bottom: 12px;
    }
    .rrp {
        font-size: 20px;
    }
    .check_availability {
        font-size: 16px !important;
    }
`;
